<template>
    <div class="my-5 flex flex-col gap-6">
        <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="max-w-3xl">
                <h3 class="text-lg leading-6 font-medium dark:text-white">
                    Iris (WhatsApp)
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Notificações WhatsApp enviadas ao comprador pelo Iris: Pix
                    Gerado e Pagamento Pendente (Payments), Acesso a E-book
                    (Kraken) e Acesso ao Mozart (Mozart). Cada tipo tem um
                    interruptor global aqui e um por seller na tela do usuário.
                    Um tipo só é enviado quando os dois estão ligados.
                </p>
            </div>
            <div v-if="!loading" class="flex flex-wrap items-center gap-2">
                <span
                    class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                    :class="
                        disabledNotificationCount === 0
                            ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
                    "
                >
                    <span
                        class="h-2 w-2 rounded-full"
                        :class="
                            disabledNotificationCount === 0
                                ? 'bg-green-500'
                                : 'bg-amber-500'
                        "
                    ></span>
                    {{ notificationsStatusLabel }}
                </span>
                <span
                    class="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                >
                    {{ effectiveDelayLabel }}
                </span>
            </div>
        </div>

        <section>
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h4
                    class="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                >
                    Disparos
                    <span
                        v-if="metrics"
                        class="ml-1 font-normal normal-case tracking-normal"
                    >
                        desde {{ metrics.since }}
                    </span>
                </h4>
                <div class="flex flex-wrap items-center gap-3">
                    <span
                        v-if="metrics && !metricsLoading"
                        class="text-xs text-gray-500 dark:text-gray-400"
                    >
                        atualizado às {{ metrics.generated_at }} (cache de
                        {{ metrics.cache_ttl_minutes }} min)
                        <button
                            type="button"
                            @click.prevent="fetchMetrics(true)"
                            class="ml-1 font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                            Atualizar agora
                        </button>
                    </span>
                    <div class="inline-flex rounded-md shadow-xs" role="group">
                    <button
                        v-for="(period, index) in periods"
                        :key="period"
                        type="button"
                        @click.prevent="changePeriod(period)"
                        :disabled="metricsLoading"
                        class="px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 transition"
                        :class="[
                            index === 0 ? 'rounded-l-md' : '-ml-px',
                            index === periods.length - 1 ? 'rounded-r-md' : '',
                            days === period
                                ? 'bg-indigo-500 text-white border-indigo-500'
                                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700',
                        ]"
                    >
                        {{ period }} dias
                    </button>
                    </div>
                </div>
            </div>

            <div
                v-if="metricsLoading"
                class="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
            >
                <card v-for="n in 5" :key="n" :loading="true" />
            </div>
            <div
                v-else-if="metricsError"
                class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
            >
                {{ metricsError }}
                <button
                    type="button"
                    @click.prevent="fetchMetrics(true)"
                    class="ml-2 font-semibold underline"
                >
                    Tentar de novo
                </button>
            </div>
            <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <metric-card
                    title="Pix Gerado"
                    :value="formatNumber(pixGenerated.sent)"
                    subtitle="avisos enviados na hora do PIX"
                />
                <metric-card
                    title="Pagamentos Pendentes"
                    :value="formatNumber(pendingPayment.sent)"
                    subtitle="lembretes enviados após o prazo"
                />
                <metric-card
                    title="Pagaram após o lembrete"
                    :value="formatNumber(conversion.paid_after_reminder)"
                    :subtitle="conversionSubtitle"
                />
                <metric-card
                    title="Falhas no Iris"
                    :value="formatNumber(totalFailed)"
                    :trend="totalFailed > 0 ? 'danger' : ''"
                    subtitle="respostas fora de 2xx (ver api_logs)"
                />
                <metric-card
                    title="Último lembrete"
                    :value="pendingPayment.last_sent_at || 'sem envios'"
                    :subtitle="
                        pixGenerated.last_sent_at
                            ? `último Pix Gerado ${pixGenerated.last_sent_at}`
                            : 'nenhum Pix Gerado no período'
                    "
                />
            </div>
        </section>

        <div class="grid gap-6 lg:grid-cols-3">
            <card class="lg:col-span-3" :loading="loading">
                <div class="flex flex-col gap-5">
                    <div>
                        <h4
                            class="text-base font-semibold text-gray-900 dark:text-white"
                        >
                            Notificações
                        </h4>
                        <p
                            class="mt-1 text-sm text-gray-500 dark:text-gray-400"
                        >
                            Interruptores globais. Desligar um tipo aqui impede
                            o envio para todos os sellers, independente da
                            configuração de cada um. Sem configuração salva,
                            todos valem como ligados.
                        </p>
                    </div>

                    <div class="grid gap-4 sm:grid-cols-2">
                        <div
                            v-for="type in notificationTypes"
                            :key="type.key"
                            class="flex items-start justify-between gap-4 rounded-lg border border-black/5 p-4 dark:border-white/10"
                        >
                            <div>
                                <p
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ type.label }}
                                    <span
                                        class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-500 dark:bg-white/10 dark:text-gray-300"
                                    >
                                        {{ type.origin }}
                                    </span>
                                </p>
                                <p
                                    class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{ type.description }}
                                </p>
                            </div>
                            <form-toggle
                                v-model="form.options.notifications[type.key]"
                                :loading="saving"
                                :label="
                                    form.options.notifications[type.key]
                                        ? 'Ligado'
                                        : 'Desligado'
                                "
                            />
                        </div>
                    </div>

                    <div
                        class="flex justify-end border-t border-black/5 pt-4 dark:border-white/10"
                    >
                        <button
                            :disabled="saving"
                            type="button"
                            @click.prevent="submit"
                            :class="{ 'cursor-not-allowed opacity-60': saving }"
                            class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                        >
                            {{ saving ? "Salvando..." : "Salvar" }}
                        </button>
                    </div>
                </div>
            </card>

            <card class="lg:col-span-2" :loading="loading">
                <form @submit.prevent="submit" class="flex flex-col gap-5">
                    <div>
                        <h4
                            class="text-base font-semibold text-gray-900 dark:text-white"
                        >
                            Lembrete de pagamento pendente
                        </h4>
                        <p
                            class="mt-1 text-sm text-gray-500 dark:text-gray-400"
                        >
                            A configuração é lida pelo Payments no momento em
                            que cada PIX é gerado. Sem configuração salva, o
                            padrão é {{ defaultDelayMinutes }} minutos.
                        </p>
                    </div>

                    <div class="grid gap-6 sm:grid-cols-2">
                        <div>
                            <label
                                for="pending_payment_delay_minutes"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                                Minutos após gerar o PIX
                            </label>
                            <div class="mt-1 flex rounded-lg shadow-xs">
                                <input
                                    type="number"
                                    id="pending_payment_delay_minutes"
                                    name="pending_payment_delay_minutes"
                                    v-model.number="
                                        form.options.pending_payment_delay_minutes
                                    "
                                    min="1"
                                    max="1440"
                                    step="1"
                                    class="block w-full rounded-l-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                                    :placeholder="String(defaultDelayMinutes)"
                                    required
                                />
                                <span
                                    class="inline-flex items-center rounded-r-lg border border-l-0 border-gray-300 bg-gray-100 px-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-300"
                                >
                                    min
                                </span>
                            </div>
                            <p
                                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                Entre 1 e 1440 (24 horas). Só envia se o PIX
                                ainda estiver pendente na hora.
                            </p>
                        </div>

                        <div class="flex flex-col justify-center">
                            <span
                                class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                                Envio do lembrete
                            </span>
                            <p class="text-sm text-gray-500 dark:text-gray-400">
                                <template v-if="form.options.notifications.pagamento_pendente">
                                    Ligado. Para desligar, use o interruptor de
                                    Pagamento Pendente no card de notificações.
                                </template>
                                <template v-else>
                                    Desligado no card de notificações: o tempo
                                    fica guardado, mas nenhum lembrete é enviado.
                                </template>
                            </p>
                        </div>
                    </div>

                    <div
                        class="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-4 dark:border-white/10"
                    >
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            <template v-if="lastChange">
                                Última alteração por
                                <strong class="font-medium">{{
                                    lastChange.by
                                }}</strong>
                                em {{ lastChange.when }}
                            </template>
                            <template v-else-if="service">
                                Configuração criada, ainda sem alterações.
                            </template>
                            <template v-else>
                                Ainda sem configuração salva: o Payments está
                                usando o padrão de
                                {{ defaultDelayMinutes }} minutos.
                            </template>
                        </p>
                        <button
                            :disabled="saving"
                            type="submit"
                            :class="{ 'cursor-not-allowed opacity-60': saving }"
                            class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                        >
                            {{ saving ? "Salvando..." : "Salvar" }}
                        </button>
                    </div>
                </form>
            </card>

            <card>
                <h4
                    class="text-base font-semibold text-gray-900 dark:text-white"
                >
                    Como funciona
                </h4>
                <ol class="mt-4 flex flex-col gap-4">
                    <li
                        v-for="(step, index) in steps"
                        :key="index"
                        class="flex gap-3"
                    >
                        <span
                            class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                        >
                            {{ index + 1 }}
                        </span>
                        <div>
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ step.title }}
                            </p>
                            <p
                                class="text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{ step.text }}
                            </p>
                        </div>
                    </li>
                </ol>
            </card>
        </div>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Card from "../../components/layouts/Card.vue";
import MetricCard from "../../components/MetricCard.vue";
import FormToggle from "../../components/forms/FormToggle.vue";

// Mesmo default do Payments (config services.iris.pending_payment.default_delay_minutes)
const DEFAULT_DELAY_MINUTES = 10;

// Mesmos event_types do enum App\Enum\Iris\IrisNotificationType.
const NOTIFICATION_TYPES = [
    {
        key: "pix_gerado",
        label: "Pix Gerado",
        origin: "Payments",
        description:
            "QR Code enviado na hora em que o PIX do checkout é gerado.",
    },
    {
        key: "pagamento_pendente",
        label: "Pagamento Pendente",
        origin: "Payments",
        description:
            "Lembrete com magic link se o PIX continuar sem pagamento após o tempo configurado.",
    },
    {
        key: "acesso_ebook",
        label: "Acesso a E-book",
        origin: "Kraken",
        description:
            "Link de download enviado quando a compra de um e-book é aprovada.",
    },
    {
        key: "acesso_mozart",
        label: "Acesso ao Mozart",
        origin: "Mozart",
        description:
            "Primeiro acesso à área de membros enviado quando o acesso é criado.",
    },
];

const DEFAULT_NOTIFICATIONS = Object.fromEntries(
    NOTIFICATION_TYPES.map((type) => [type.key, true])
);

const DEFAULT_OPTIONS = {
    // Chave legada lida pelo Payments antigo; mantida em sincronia com notifications.pagamento_pendente.
    pending_payment_enabled: true,
    pending_payment_delay_minutes: DEFAULT_DELAY_MINUTES,
    notifications: { ...DEFAULT_NOTIFICATIONS },
};

const EMPTY_EVENT = { sent: 0, failed: 0, last_sent_at: null };

export default {
    components: { Card, MetricCard, FormToggle },
    data() {
        return {
            loading: false,
            saving: false,
            service: null,
            form: {
                type: "iris",
                options: {
                    ...DEFAULT_OPTIONS,
                    notifications: { ...DEFAULT_NOTIFICATIONS },
                },
            },
            notificationTypes: NOTIFICATION_TYPES,
            periods: [7, 30, 90],
            days: 30,
            metrics: null,
            metricsLoading: false,
            metricsError: null,
            defaultDelayMinutes: DEFAULT_DELAY_MINUTES,
            steps: [
                {
                    title: "PIX gerado no checkout",
                    text: "O Payments manda o aviso de Pix Gerado com o QR Code na hora e agenda o lembrete.",
                },
                {
                    title: "Espera o tempo configurado",
                    text: "O job aguarda os minutos definidos aqui, lidos no momento em que o PIX nasce.",
                },
                {
                    title: "Confere se ainda está pendente",
                    text: "Pagou, expirou ou o produtor desligou o Iris: nada é enviado.",
                },
                {
                    title: "Envia o Pagamento Pendente",
                    text: "O Iris manda o WhatsApp com o magic link (/r/{hash do produto}). Tudo fica em api_logs.",
                },
            ],
        };
    },
    computed: {
        disabledNotificationCount() {
            return NOTIFICATION_TYPES.filter(
                (type) => !this.form.options.notifications[type.key]
            ).length;
        },
        notificationsStatusLabel() {
            if (this.disabledNotificationCount === 0) {
                return "Todas as notificações ligadas";
            }

            return this.disabledNotificationCount === 1
                ? "1 notificação desligada"
                : `${this.disabledNotificationCount} notificações desligadas`;
        },
        pixGenerated() {
            return this.metrics?.events?.pix_gerado ?? EMPTY_EVENT;
        },
        pendingPayment() {
            return this.metrics?.events?.pagamento_pendente ?? EMPTY_EVENT;
        },
        conversion() {
            return (
                this.metrics?.conversion ?? {
                    reminded_orders: 0,
                    paid_after_reminder: 0,
                    rate: 0,
                }
            );
        },
        totalFailed() {
            return this.pixGenerated.failed + this.pendingPayment.failed;
        },
        conversionSubtitle() {
            if (!this.conversion.reminded_orders) {
                return "nenhum pedido lembrado no período";
            }

            return `${this.conversion.rate}% de ${this.formatNumber(
                this.conversion.reminded_orders
            )} pedidos lembrados`;
        },
        effectiveDelayLabel() {
            const minutes = Number(
                this.form.options.pending_payment_delay_minutes
            );
            const effective =
                Number.isInteger(minutes) && minutes >= 1
                    ? minutes
                    : DEFAULT_DELAY_MINUTES;

            return this.service
                ? `${effective} min após o PIX`
                : `Padrão: ${effective} min após o PIX`;
        },
        lastChange() {
            const history = this.service?.history;

            if (!history?.when) {
                return null;
            }

            return {
                by:
                    history.performed_by?.user_name ||
                    history.performed_by?.user_email ||
                    "usuário não identificado",
                when: this.formatDateTime(history.when),
            };
        },
    },
    mounted() {
        this.fetch("iris");
        this.fetchMetrics();
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    methods: {
        fetch(type) {
            this.loading = true;
            axios
                .get(`/api/services/type/${type}`)
                .then((response) => {
                    // Primeiro acesso: ainda não existe linha type=iris na tabela services
                    if (response.data && Object.keys(response.data).length) {
                        this.service = response.data;
                        this.form.options = this.mergeOptions(
                            response.data.options || {}
                        );
                    }
                })
                .catch((error) => console.error(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchMetrics(refresh = false) {
            this.metricsLoading = true;
            this.metricsError = null;
            axios
                .get(`/api/services/iris/metrics`, {
                    params: { days: this.days, refresh: refresh ? 1 : 0 },
                })
                .then((response) => {
                    this.metrics = response.data;
                })
                .catch((error) => {
                    console.error(error);
                    this.metricsError =
                        error?.response?.data?.message ??
                        "Não foi possível carregar as métricas do Iris.";
                })
                .finally(() => {
                    this.metricsLoading = false;
                });
        },
        // Ausente = ligado (mesma regra dos serviços de envio). Configuração antiga
        // sem `notifications` herda o lembrete da chave legada pending_payment_enabled.
        mergeOptions(saved) {
            const notifications = {
                ...DEFAULT_NOTIFICATIONS,
                ...(saved.notifications || {}),
            };

            if (
                !saved.notifications &&
                typeof saved.pending_payment_enabled !== "undefined"
            ) {
                notifications.pagamento_pendente = Boolean(
                    saved.pending_payment_enabled
                );
            }

            return { ...DEFAULT_OPTIONS, ...saved, notifications };
        },
        changePeriod(period) {
            if (this.days === period) {
                return;
            }

            this.days = period;
            this.fetchMetrics();
        },
        submit() {
            const minutes = Number(
                this.form.options.pending_payment_delay_minutes
            );

            if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
                this.toast.error(
                    "Informe um número inteiro de minutos entre 1 e 1440."
                );
                return;
            }

            this.form.options.pending_payment_delay_minutes = minutes;
            NOTIFICATION_TYPES.forEach((type) => {
                this.form.options.notifications[type.key] = Boolean(
                    this.form.options.notifications[type.key]
                );
            });
            this.form.options.pending_payment_enabled =
                this.form.options.notifications.pagamento_pendente;

            this.saving = true;

            const request = this.service
                ? axios.put(`/api/services/${this.service.id}`, this.form)
                : axios.post(`/api/services/`, this.form);

            request
                .then(({ data }) => {
                    if (data) {
                        this.toast.success(
                            "Configuração do Iris salva com sucesso"
                        );
                        this.fetch("iris");
                    }
                })
                .catch((error) => {
                    this.toast.error(
                        error?.response?.data?.message ??
                            "Não foi possível salvar a configuração do Iris."
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        formatNumber(value) {
            return new Intl.NumberFormat("pt-BR").format(Number(value) || 0);
        },
        formatDateTime(value) {
            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return String(value);
            }

            return date.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
    },
};
</script>
